import type { TFunction } from 'i18next';

import { errorMessage } from '@feathr/hooks';
import { validate } from '@feathr/rachis';

export function validateName({
  oldName,
  newName,
}: {
  oldName: string;
  newName?: string;
}): string[] {
  const error = validate(
    { oldName, newName },
    {
      newName: {
        presence: {
          allowEmpty: false,
          message: '^Name cannot be empty',
        },
        equality: {
          attribute: 'oldName',
          message: '^New name cannot be the same as the current name',
          comparator: (value: string, other: string) => value !== other,
        },
      },
    },
    { format: 'flat' },
  );
  return error;
}

/**
 * Formats validation errors to be more user-friendly
 */
export function formatValidationError(error: unknown, t: TFunction): string {
  if (!error) {
    return t('An unknown error occurred!');
  }

  const errorString = error.toString();

  // Check if this is a Pydantic ValidationError
  if (
    errorString.includes('ValidationError') &&
    errorString.includes('validation errors for FormContent')
  ) {
    // Handle deprecated form structure errors
    if (errorString.includes('Field required [type=missing') && errorString.includes('rows')) {
      return t(
        'This form was created with an older version of Feathr and cannot be renamed. Please create a new form instead.',
      );
    }

    if (errorString.includes('Extra inputs are not permitted') && errorString.includes('fields')) {
      return t(
        'This form was created with an older version of Feathr and cannot be renamed. Please create a new form instead.',
      );
    }

    // Handle other validation errors
    if (errorString.includes('Field required')) {
      return t('The form is missing required fields and cannot be renamed.');
    }

    // Generic validation error
    return t(
      'The form has validation errors and cannot be renamed. This may be due to the form being created with an older version of Feathr.',
    );
  }

  // For other types of errors, reuse the shared error message helper for consistency
  return errorMessage(error, t);
}
